import { useState, type FormEvent } from 'react';
import { siteMeta } from '@/mocks/cinema';

const contactCards = [
  { icon: 'ri-mail-line', label: '投稿邮箱', value: 'tougao@chiguayingyuan.com', note: '票房数据 / 排片动态 / 档期线索' },
  { icon: 'ri-chat-3-line', label: '合作洽谈', value: 'bd@chiguayingyuan.com', note: '影城 / 片方 / 媒体商务合作' },
  { icon: 'ri-community-line', label: '影迷社群', value: '吃瓜影迷群', note: '每周新片与票房吃瓜交流' },
];

const values = [
  { icon: 'ri-database-2-line', title: '数据先行', text: '坚持以真实票房与场次口径为准，把冷冰冰的数字讲成看得懂的行情。' },
  { icon: 'ri-speed-line', title: '实时更新', text: '票房、排片与上映名单分时刷新，让你第一时间吃到最新鲜的瓜。' },
  { icon: 'ri-honour-line', title: '客观中立', text: '不吹不黑，只呈现事实与趋势，把判断交给观众自己。' },
  { icon: 'ri-heart-3-line', title: '懂你所需', text: '从想看到开映，围绕选片与购票的真实决策场景服务每一位影迷。' },
];

const SUBMIT_URL = 'https://readdy.ai/api/form/daf8a4bep053ij1kv0';

export default function About() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [phoneAlt, setPhoneAlt] = useState('');
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 蜜罐字段：已填入则为机器人，静默通过
    const honeypot = (fd.get('phone_alt') as string) || '';
    if (honeypot.trim()) {
      setStatus('success');
      setSubmitted(true);
      return;
    }
    fd.delete('phone_alt');

    const body = new URLSearchParams();
    fd.forEach((value, key) => {
      if (typeof value === 'string' && value.trim()) body.append(key, value);
    });

    setStatus('loading');
    try {
      const res = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;
      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setSubmitted(true);
        form.reset();
        setPhoneAlt('');
        setFormError('');
      } else {
        setStatus('error');
        setFormError(
          typeof serverMsg === 'string' && serverMsg
            ? serverMsg
            : '提交失败，请稍后重试或改用投稿邮箱。',
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，请稍后重试或改用投稿邮箱。');
    }
  }

  return (
    <section id="about" className="bg-background-50">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-600 text-background-50">
                <i className="ri-movie-2-fill text-xl"></i>
              </span>
              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  关于 {siteMeta.name}
                </h2>
                <p className="text-xs text-foreground-500">你的电影情报与排片参谋</p>
              </div>
            </div>

            <p className="mt-5 text-sm leading-relaxed text-foreground-700">
              {siteMeta.name}
              是一站式电影票房与排片资讯平台。我们实时汇总吃瓜影院票房数字、排片分布与上映名单，从新片首映到黄金档期窗口，
              帮你把"这周该看什么、什么时间看、哪一场划算"这些决定变得更简单。
            </p>

            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {values.map((v) => (
                <div
                  key={v.title}
                  className="rounded-lg border border-background-200 bg-background-100 p-4"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${v.icon} text-lg`}></i>
                  </span>
                  <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950">{v.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{v.text}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              {contactCards.map((c) => (
                <div
                  key={c.label}
                  className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-800">
                    <i className={`${c.icon} text-lg`}></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">{c.label}</p>
                    <p className="text-sm font-medium text-foreground-900">{c.value}</p>
                    <p className="text-xs text-foreground-500">{c.note}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h3 className="font-heading text-xl font-bold text-foreground-950">投稿 / 联系</h3>
              <p className="mt-1.5 text-sm text-foreground-600">
                有票房线索、排片动态或合作意向？填下表单，我们会尽快与你联系。
              </p>

              {submitted ? (
                <div className="mt-6 rounded-lg border border-primary-200 bg-primary-50 p-6 text-center">
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-600 text-background-50">
                    <i className="ri-check-line text-xl"></i>
                  </span>
                  <h4 className="mt-3 font-heading text-lg font-bold text-primary-800">提交成功</h4>
                  <p className="mt-1.5 text-sm text-foreground-600">
                    感谢你的来信，我们已收到你的投稿，核实后会尽快处理。
                  </p>
                </div>
              ) : (
                <form
                  id="contact-form"
                  data-readdy-form={SUBMIT_URL}
                  onSubmit={handleSubmit}
                  className="mt-5 space-y-4"
                >
                  <div>
                    <label htmlFor="name" className="mb-1 block text-sm font-medium text-foreground-800">
                      你的称呼
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      maxLength={50}
                      placeholder="怎么称呼你"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-1 block text-sm font-medium text-foreground-800">
                      联系邮箱
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      maxLength={100}
                      placeholder="name@example.com"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="mb-1 block text-sm font-medium text-foreground-800">
                      投稿分类
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                    >
                      <option value="票房数据线索">票房数据线索</option>
                      <option value="排片 / 上映动态">排片 / 上映动态</option>
                      <option value="档期窗口消息">档期窗口消息</option>
                      <option value="商务合作">商务合作</option>
                      <option value="意见建议">意见建议</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="message" className="mb-1 block text-sm font-medium text-foreground-800">
                      详细内容
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      placeholder="请简要描述你的投稿或需求（500 字以内）"
                      className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                    ></textarea>
                  </div>
                  <div className="h-0 w-0 overflow-hidden" aria-hidden="true">
                    <label htmlFor="phone_alt">请不要填写此项</label>
                    <input
                      id="phone_alt"
                      name="phone_alt"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      readOnly
                      value={phoneAlt}
                      onChange={(e) => setPhoneAlt(e.target.value)}
                    />
                  </div>
                  {formError && (
                    <p className="rounded-md bg-primary-50 px-3 py-2 text-sm text-primary-800">
                      <i className="ri-error-warning-line mr-1"></i>
                      {formError}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-4 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {status === 'loading' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        提交中…
                      </>
                    ) : (
                      <>
                        提交投稿
                        <i className="ri-send-plane-line"></i>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}